import httpUtility from '../utils/http.utility.js'

import apiClient from './client.js'

async function getCurrentUser() {
  const response = await apiClient.get('/user/getCurrentUser')
  return response.data.currentUser
}

async function getAccountUsers(search = '', { page, pageSize, signal } = {}) {
  const response = await apiClient.get(`/user/searchAccountUsers/?${httpUtility.search({ search, page, pageSize })}`, { signal })
  return { items: response.data.users ?? response.data.items ?? [], total: response.data.total }
}

async function getAllUsers(search = '', { page, pageSize, signal } = {}) {
  const response = await apiClient.get(`/user/search-accessible-users/?${httpUtility.search({ search, page, pageSize })}`, { signal })
  return { items: response.data.users ?? response.data.items ?? [], total: response.data.total }
}

async function getAdminUserList(filters = {}, { page, pageSize, signal } = {}) {
  const response = await apiClient.get(
    `/user/admin/list?${httpUtility.query({
      search: filters.search,
      role: filters.role,
      active: filters.active,
      page,
      itemsPerPage: pageSize,
    })}`,
    { signal },
  )
  return {
    items: response.data.users ?? [],
    total: response.data.totalLength ?? 0,
    page: response.data.page ?? page,
    pageSize: response.data.itemsPerPage ?? pageSize,
  }
}

async function updateAdminUserRole(userId, role) {
  const response = await apiClient.patch(`/user/admin/${userId}/role`, { role })
  return response.data.user
}

async function updateAdminUserActive(userId, active) {
  const response = await apiClient.patch(`/user/admin/${userId}/active`, { active })
  return response.data.user
}

async function forceLogoutAdminUser(userId) {
  const response = await apiClient.delete(`/user/admin/${userId}/session`)
  return response.data.user
}

async function getAdminUser(userId) {
  const response = await apiClient.get(`/user/admin/${userId}`)
  return response.data.user
}

async function updateAdminUser(userId, payload) {
  const response = await apiClient.patch(`/user/admin/${userId}`, payload)
  return response.data.user
}

async function listAdminUserAccounts(userId) {
  const response = await apiClient.get(`/user/admin/${userId}/accounts`)
  return response.data.accounts ?? []
}

async function addAdminUserAccount(userId, accountId) {
  const response = await apiClient.post(`/user/admin/${userId}/accounts`, { accountId })
  return response.data.account
}

async function removeAdminUserAccount(userId, accountId) {
  const response = await apiClient.delete(`/user/admin/${userId}/accounts/${accountId}`)
  return response.data
}

async function getTicketUsers(search = '', { accountId, page, pageSize, signal, includeAccess } = {}) {
  const response = await apiClient.get(
    `/user/picker/ticket-users?${httpUtility.search({ search, page, pageSize, accountId, includeAccess })}`,
    { signal },
  )
  return { items: response.data.items, total: response.data.total }
}

async function getAccountsTicketUsers(search = '', { accountIds, page, pageSize, signal, includeAccess } = {}) {
  const response = await apiClient.post(
    '/user/picker/ticket-users-accounts',
    httpUtility.searchBody({ search, page, pageSize, accountIds, includeAccess }),
    { signal },
  )
  return { items: response.data.items, total: response.data.total }
}

async function getAllTicketUsers(search = '', { page, pageSize, signal, includeAccess } = {}) {
  const response = await apiClient.get(
    `/user/picker/ticket-users-all?${httpUtility.search({ search, page, pageSize, includeAccess })}`,
    { signal },
  )
  return { items: response.data.items, total: response.data.total }
}

async function getTicketGroupUsers(search = '', { ticketGroupId, ticketGroupIds, page, pageSize, signal, includeAccess } = {}) {
  const response = await apiClient.get(
    `/user/picker/ticket-group-users?${httpUtility.search({ search, page, pageSize, ticketGroupId, ticketGroupIds, includeAccess })}`,
    { signal },
  )
  return { items: response.data.items, total: response.data.total }
}

async function forgotPassword(email) {
  return apiClient.post('/user/forgotPassword/', { email })
}

async function updateGroupmode() {
  const response = await apiClient.get('/user/set-group-mode/')
  return response.data.groupMode
}

async function updateNotificationSettings(notificationSettings) {
  const response = await apiClient.patch('/user/notification-settings/', { notificationSettings })
  return response.data.notificationSettings
}

class UserService {
  get = {
    /**
     * Load the currently authenticated user.
     *
     * @returns {Promise<object|null>} Unwrapped from `response.data.currentUser`.
     */
    async current() {
      return getCurrentUser()
    },

    account: {
      /**
       * Paginated search of users in the current account.
       *
       * @param {string} [search='']
       * @param {object} [params]
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>} Unwrapped from `response.data`.
       */
      async search(search = '', { page, pageSize, signal } = {}) {
        return getAccountUsers(search, { page, pageSize, signal })
      },
    },

    all: {
      /**
       * Paginated search of users across every account the caller
       * can access.
       *
       * @param {string} [search='']
       * @param {object} [params]
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>} Unwrapped from `response.data`.
       */
      async search(search = '', { page, pageSize, signal } = {}) {
        return getAllUsers(search, { page, pageSize, signal })
      },
    },

    admin: {
      /**
       * SuperAdmin paginated user dump (`GET /user/admin/list`).
       *
       * @param {object} [filters]
       * @param {string} [filters.search]
       * @param {string} [filters.role]
       * @param {string|boolean} [filters.active]
       * @param {object} [params]
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number, page?: number, pageSize?: number }>}
       */
      async list(filters = {}, params = {}) {
        return getAdminUserList(filters, params)
      },

      /**
       * SuperAdmin single user (`GET /user/admin/:userId`).
       *
       * @param {string} userId
       * @returns {Promise<object>}
       */
      async one(userId) {
        return getAdminUser(userId)
      },

      /**
       * Accounts where the user is a member (`GET /user/admin/:userId/accounts`).
       *
       * @param {string} userId
       * @returns {Promise<object[]>}
       */
      async accounts(userId) {
        return listAdminUserAccounts(userId)
      },
    },
  }

  admin = {
    update: {
      /**
       * Change a user's role (superAdmin only; superAdmin role not assignable).
       *
       * @param {string} userId
       * @param {string} role
       * @returns {Promise<{ _id: string, role: string }>}
       */
      async role(userId, role) {
        return updateAdminUserRole(userId, role)
      },

      /**
       * Enable or disable a user (superAdmin only).
       *
       * @param {string} userId
       * @param {boolean} active
       * @returns {Promise<{ _id: string, active: boolean }>}
       */
      async active(userId, active) {
        return updateAdminUserActive(userId, active)
      },

      /**
       * Force-logout a user (clear activeSessionId + auth Redis session).
       *
       * @param {string} userId
       * @returns {Promise<{ _id: string, activeSessionId: null }>}
       */
      async session(userId) {
        return forceLogoutAdminUser(userId)
      },

      /**
       * SuperAdmin profile/settings save (`PATCH /user/admin/:userId`).
       *
       * @param {string} userId
       * @param {object} payload
       * @returns {Promise<object>}
       */
      async profile(userId, payload) {
        return updateAdminUser(userId, payload)
      },

      /**
       * Add membership (`POST /user/admin/:userId/accounts`).
       *
       * @param {string} userId
       * @param {string} accountId
       * @returns {Promise<{ _id: string, name?: string }>}
       */
      async addAccount(userId, accountId) {
        return addAdminUserAccount(userId, accountId)
      },

      /**
       * Remove membership (`DELETE /user/admin/:userId/accounts/:accountId`).
       *
       * @param {string} userId
       * @param {string} accountId
       * @returns {Promise<{ accountId: string }>}
       */
      async removeAccount(userId, accountId) {
        return removeAdminUserAccount(userId, accountId)
      },
    },
  }

  picker = {
    assignee: {
      /**
       * Account mode (`GET /user/picker/ticket-users`) — pool for one
       * account (members ∪ host ticket-group members). Backs the account
       * filter and the ticket editor in both layouts.
       *
       * @param {string} [search]
       * @param {object} params
       * @param {string} [params.accountId] - Defaults server-side to the current account.
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>}
       */
      async account(search, params) {
        return getTicketUsers(search, params)
      },

      /**
       * Accounts mode (`POST /user/picker/ticket-users-accounts`) — pool
       * across an explicit account subset (accounts / org-group filter).
       * Server validates `accountIds ⊆ allowed`.
       *
       * @param {string} [search]
       * @param {object} params
       * @param {string[]} params.accountIds
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>}
       */
      async accounts(search, params) {
        return getAccountsTicketUsers(search, params)
      },

      /**
       * All / Kaikki mode (`GET /user/picker/ticket-users-all`) — pool
       * across the caller's full allowed account set (unbounded for
       * superAdmin). Account set is server-derived; no params beyond search.
       *
       * @param {string} [search]
       * @param {object} [params]
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>}
       */
      async all(search, params) {
        return getAllTicketUsers(search, params)
      },

      /**
       * Ticket-group mode (`GET /user/picker/ticket-group-users`) — pool
       * across the selected group's accounts ∪ the group's users. Server
       * 403s when the caller isn't a member.
       *
       * @param {string} [search]
       * @param {object} params
       * @param {string} params.ticketGroupId
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       * @returns {Promise<{ items: object[], total: number }>}
       */
      async ticketGroup(search, params) {
        return getTicketGroupUsers(search, params)
      },
    },
  }

  password = {
    /**
     * Request a password-reset email for one address.
     *
     * @param {string} email
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async forgot(email) {
      return forgotPassword(email)
    },
  }

  update = {
    /**
     * Toggle the signed-in user's group-mode flag.
     *
     * @returns {Promise<boolean>} Unwrapped from `response.data.groupMode`.
     */
    async groupmode() {
      return updateGroupmode()
    },

    /**
     * Persist ticket/trigger email prefs for the signed-in user.
     *
     * @param {object} notificationSettings
     * @returns {Promise<object>}
     */
    async notificationSettings(notificationSettings) {
      return updateNotificationSettings(notificationSettings)
    },
  }
}

export default new UserService()
